import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { config } from 'shared/config';
import { clearSharedReadResources } from './readResource';
import { sourceIdentityGeneration } from './sourceIdentity';
import { EDIT_SESSION_HEADER, editSessionId } from './editSession';
import { followReadJob, isReadJobResponse } from './readJobs';
import { clearSourceRecoveries, recoverSourceAuth, reportSourceIssue, requestReportId, sourceIssue, sourceAuthPayload, sourceReadRequest } from './sourceAuthRecovery';

export type SessionUser = {
    userId: string; email: string; firstName?: string; lastName?: string;
    orgId?: string; orgName?: string; platformAdminTag?: string;
    subscriptionLevel?: string; demoUser?: boolean;
};
/** ``subscribed``: the server's subscription decision for the subscription routes (null when it could not be checked). */
export type Session = { user: SessionUser; csrfToken: string; subscribed?: boolean | null };
type SessionRequestConfig = AxiosRequestConfig & { sessionGeneration?: number; sessionConfirmation?: boolean };
let session: Session | null = null;
let pending: Promise<Session> | undefined;
let generation = 0;
let signedOutError: Error | undefined;
let ended = false;
const configured = new WeakSet<object>();
export const SESSION_CHANGED = 'unity-session-changed';
const publicPaths = new Set([
    '/login', '/register', '/api/auth/registration/start', '/api/auth/registration/verify',
    '/api/auth/register-and-checkout', '/api/auth/email-login', '/api/auth/email-login/verify',
    '/api/auth/google', '/api/auth/request-password-reset', '/api/auth/reset-password',
    '/api/contact', '/api/page-hit', '/api/auth/session', '/api/auth/validate',
]);
const sessionIssuerPaths = new Set([
    '/login', '/register', '/api/auth/register-and-checkout', '/api/auth/email-login/verify', '/api/auth/google',
]);

export class SessionConfirmationError extends Error {
    constructor(public readonly unavailable: boolean) {
        super(unavailable
            ? "Sign-in succeeded, but we couldn't check your session. Please try signing in again."
            : "Sign-in succeeded, but this browser couldn't keep your session. Please try again or contact support.");
        this.name = 'SessionConfirmationError';
    }
}

export const signInErrorMessage = (error: any, fallback: string): string =>
    error instanceof SessionConfirmationError ? error.message : error.response?.data?.message || fallback;

export const isUnityRequest = (url?: string, baseURL?: string): boolean => {
    try { return new URL(url || '', baseURL || window.location.origin).origin === new URL(config.API_URL).origin; }
    catch { return false; }
};

/** `expired`: the server refused this browser's session (a 401), rather than the person signing out. */
export const forgetSession = (expired = false): void => {
    // A sign-out is never an expiry, even after the server already ended the session.
    ended = expired ? (hasSessionHint() || ended) : false;
    generation += 1;
    session = null;
    pending = undefined;
    signedOutError = Object.assign(new Error('Please sign in to continue.'), { response: { status: 401 } });
    try { localStorage.removeItem('user'); } catch { /* Display storage is optional. */ }
    clearSharedReadResources();
    clearSourceRecoveries();
    window.dispatchEvent(new Event(SESSION_CHANGED));
};

const acceptSession = (next: Session): void => {
    signedOutError = undefined;
    ended = false;
    if (session?.user.userId !== next.user.userId) {
        generation += 1;
        clearSourceRecoveries();
        clearSharedReadResources();
    }
    session = next;
    // Display-only account information. The cookie is the credential and stays HttpOnly.
    try { localStorage.setItem('user', JSON.stringify(next.user)); } catch { /* The HttpOnly cookie is the credential. */ }
    window.dispatchEvent(new Event(SESSION_CHANGED));
};

/** Whether this browser has signed in (the display copy of the user): false means the session lookup would only answer 401. */
export const hasSessionHint = (): boolean => { try { return localStorage.getItem('user') !== null; } catch { return false; } };
/** The session this browser was signed in with expired or was revoked (not a sign-out); the sign-in page says so. */
export const sessionExpired = (): boolean => ended;

export const ensureSession = (): Promise<Session> => {
    if (session) return Promise.resolve(session);
    if (signedOutError) return Promise.reject(signedOutError);
    if (!pending) {
        const started = generation;
        const read = axios.get<Session>(`${config.API_URL}/api/auth/session`, { withCredentials: true, timeout: 15000 })
            .then(response => {
                if (started !== generation && session?.user.userId !== response.data.user?.userId) throw new axios.CanceledError('Account changed');
                if (!response.data.user?.userId || !response.data.csrfToken) throw new Error('Invalid session response');
                acceptSession(response.data);
                return response.data;
            })
            .finally(() => { if (pending === read) pending = undefined; });
        pending = read;
    }
    return pending;
};

/** A login response cannot prove that the browser accepted its Set-Cookie header.
 * Confirm the exact issued session over the network before accepting it or navigating.
 */
const confirmIssuedSession = async (issued: Session): Promise<void> => {
    generation += 1;
    const started = generation;
    session = null;
    pending = undefined;
    signedOutError = undefined;
    clearSourceRecoveries();
    clearSharedReadResources();
    try { localStorage.removeItem('user'); } catch { /* Display storage is optional. */ }
    const read = axios.get<Session>(`${config.API_URL}/api/auth/session`, {
        withCredentials: true, timeout: 15000, sessionConfirmation: true,
    } as SessionRequestConfig)
        .then(response => {
            if (started !== generation) throw new axios.CanceledError('Account changed');
            if (response.data.user?.userId?.toLowerCase() !== issued.user.userId.toLowerCase() || response.data.csrfToken !== issued.csrfToken) {
                throw new SessionConfirmationError(false);
            }
            acceptSession(response.data);
            return response.data;
        })
        .catch(error => {
            if (axios.isCancel(error)) throw error;
            if (started !== generation) throw new axios.CanceledError('Account changed');
            forgetSession();
            if (error instanceof SessionConfirmationError) throw error;
            throw new SessionConfirmationError(error.response?.status !== 401);
        })
        .finally(() => { if (pending === read) pending = undefined; });
    pending = read;
    await read;
};

export const configureAuthenticatedClient = (client: AxiosInstance): void => {
    if (configured.has(client) || !client.interceptors) return;
    configured.add(client);
    client.interceptors.request.use(async request => {
        if (!isUnityRequest(request.url, request.baseURL)) return request;
        request.withCredentials = true;
        const path = new URL(request.url || '', request.baseURL || config.API_URL).pathname;
        const unsafe = !['get', 'head', 'options'].includes((request.method || 'get').toLowerCase());
        if (unsafe && !publicPaths.has(path)) {
            const active = await ensureSession();
            request.headers.set('X-CSRF-Token', active.csrfToken);
            // Every write names this tab's edit session, so the server joins it to the person's lease on the page or chart it changes.
            request.headers.set(EDIT_SESSION_HEADER, editSessionId());
        }
        (request as AxiosRequestConfig & { sessionGeneration?: number }).sessionGeneration = generation;
        if (sourceReadRequest(request.method || 'get', path)) (request as any).sourceGeneration = sourceIdentityGeneration();
        return request;
    });
    const recover = async (response: any) => {
        const payload = sourceAuthPayload(response.data);
        const issue = sourceIssue(payload);
        if (!issue || !isUnityRequest(response.config.url, response.config.baseURL)) return null;
        const options = response.config;
        const uri = new URL(options.url || '', options.baseURL || config.API_URL);
        if (uri.pathname.includes('/browser-auth/') || uri.pathname.includes('/analysis-services/auth/')) return null;
        const reportId = options.params?.reportId || requestReportId(uri.href, options.data);
        const keys: string[] = options.sourceAuthRetriedKeys || [];
        const key = issue.identityKey || `connection:${issue.connectionId}`;
        if (!sourceReadRequest(options.method || 'get', uri.pathname) || keys.includes(key)) {
            reportSourceIssue(payload, reportId); return null;
        }
        const ok = await recoverSourceAuth(payload, { reportId, signal: options.signal, readKey: options.readJobKey });
        if (options.sessionGeneration !== generation || options.signal?.aborted) throw new axios.CanceledError('This read is no longer needed.');
        return ok ? client.request({ ...options, sourceAuthRetriedKeys: [...keys, key] }) : null;
    };
    client.interceptors.response.use(async response => {
        if (!isUnityRequest(response.config.url, response.config.baseURL)) return response;
        const path = new URL(response.config.url || '', response.config.baseURL || config.API_URL).pathname;
        // Session bootstrap owns its response, including abandoned bootstrap reads.
        if (path !== '/api/auth/session' &&
            (response.config as AxiosRequestConfig & { sessionGeneration?: number }).sessionGeneration !== generation) {
            throw new axios.CanceledError('Account changed');
        }
        if (sessionIssuerPaths.has(path)) {
            if (!response.data?.user?.userId || !response.data?.csrfToken) throw new SessionConfirmationError(false);
            await confirmIssuedSession(response.data);
        }
        if (isReadJobResponse(response.status, response.data) && !path.startsWith('/api/read-jobs/')) {
            // A long read continues as a read job (DP-031): resolve with its own final response.
            const key = (response.config as AxiosRequestConfig & { readJobKey?: string }).readJobKey;
            const outcome = await followReadJob(response.data.readJob, { send: authenticatedFetch, signal: response.config.signal as AbortSignal | undefined, key });
            const settled = { ...response, status: outcome.status, statusText: '', data: outcome.data };
            if ((response.config as any).sourceGeneration != null && (response.config as any).sourceGeneration !== sourceIdentityGeneration()) throw new axios.CanceledError('Source account changed');
            const recovered = await recover(settled);
            if (recovered) return recovered;
            if ((response.config as any).sourceGeneration != null && (response.config as any).sourceGeneration !== sourceIdentityGeneration()) throw new axios.CanceledError('Source account changed');
            if (outcome.status >= 400) throw new AxiosError(outcome.data?.error || `Request failed with status code ${outcome.status}`,
                outcome.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
                response.config, response.request, settled);
            return settled;
        }
        if ((response.config as any).sourceGeneration != null && (response.config as any).sourceGeneration !== sourceIdentityGeneration()) throw new axios.CanceledError('Source account changed');
        const recovered = await recover(response);
        if (recovered) return recovered;
        if ((response.config as any).sourceGeneration != null && (response.config as any).sourceGeneration !== sourceIdentityGeneration()) throw new axios.CanceledError('Source account changed');
        return response;
    }, async error => {
        if (error.config?.sourceGeneration != null && error.config.sourceGeneration !== sourceIdentityGeneration()) {
            return Promise.reject(new axios.CanceledError('Source account changed'));
        }
        if (error.response && error.config?.sessionGeneration === generation) {
            const recovered = await recover(error.response);
            if (recovered) return recovered;
        }
        if (isUnityRequest(error.config?.url, error.config?.baseURL) && error.response?.status === 401) {
            const path = new URL(error.config.url || '', error.config.baseURL || config.API_URL).pathname;
            const started = error.config.sessionGeneration;
            if ((!publicPaths.has(path) || path === '/api/auth/session') && started === generation &&
                !(error.config as SessionRequestConfig).sessionConfirmation) forgetSession(true);
        }
        return Promise.reject(error);
    });
};

configureAuthenticatedClient(axios);

export const authenticatedFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
    if (!isUnityRequest(url)) throw new Error('Authenticated fetch requires a Unity API URL.');
    const active = await ensureSession();
    const started = generation;
    const sourceStarted = sourceIdentityGeneration();
    const headers = new Headers(init.headers);
    if (!['GET', 'HEAD', 'OPTIONS'].includes((init.method || 'GET').toUpperCase())) headers.set('X-CSRF-Token', active.csrfToken);
    const response = await fetch(url, { ...init, headers, credentials: 'include' });
    if (started !== generation) throw new DOMException('Account changed', 'AbortError');
    if (sourceReadRequest(init.method || 'GET', new URL(url).pathname) && sourceStarted !== sourceIdentityGeneration()) throw new DOMException('Source account changed', 'AbortError');
    if (response.status === 401) forgetSession(true);
    return response;
};

/** Send a request that must outlive the page, from a `pagehide` handler. The browser cancels an ordinary
 * request when the page goes away; a keepalive fetch continues. It is sent at once with the session already
 * known, because an await may not finish before the page is torn down. Returns false when no session is
 * known or the browser refuses it; the caller needs another way to finish the work, since even a sent
 * request can be refused (some browsers do not preflight keepalive requests across origins).
 */
export const sendKeepalive = (url: string, init: RequestInit = {}): boolean => {
    if (!session || !isUnityRequest(url)) return false;
    const headers = new Headers(init.headers);
    if (!['GET', 'HEAD', 'OPTIONS'].includes((init.method || 'GET').toUpperCase())) {
        headers.set('X-CSRF-Token', session.csrfToken);
        headers.set(EDIT_SESSION_HEADER, editSessionId());
    }
    try {
        void fetch(url, { ...init, headers, credentials: 'include', keepalive: true }).catch(() => undefined);
        return true;
    } catch {
        return false;
    }
};

export const signOut = async (everywhere = false): Promise<void> => {
    try { await axios.post(`${config.API_URL}/api/auth/${everywhere ? 'logout-all' : 'logout'}`, {}); }
    catch (error: any) { if (error.response?.status !== 401) throw error; }
    forgetSession();
    // Clear all mounted report state, streams and per-visual caches on account changes.
    window.location.assign('/login');
};

window.addEventListener('storage', event => {
    if (event.key === 'user') {
        // Signed out (or expired) in another tab: this one signs out too and remembers no return; that tab keeps its own.
        if (event.newValue === null) { if (!hasSessionHint()) forgetSession(); return; }
        let nextId: string | undefined;
        try { nextId = JSON.parse(event.newValue)?.userId; } catch { /* Invalid display cache is not a session. */ }
        if (nextId !== session?.user.userId) window.location.reload();
    }
});
